import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from './api';
import ProductNav from './components/ProductNav';
import SideHelpPanel from './components/SideHelpPanel';
import { glassSurface } from './config/glassTheme';

const money = (cents) => cents == null ? 'Not available' : `$${(cents / 100).toFixed(2)}`;

const PAGE_CSS = `
  .ggp-grid-3 { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:18px; }
  .ggp-plan-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(205px,1fr)); gap:14px; }
  .ggp-timeline { display:grid; grid-template-columns:repeat(5,minmax(120px,1fr)); gap:12px; }
  .ggp-table-wrap { overflow-x:auto; border:1px solid rgba(255,255,255,.1); border-radius:14px; }
  .ggp-table { width:100%; min-width:920px; border-collapse:collapse; color:#fff; }
  .ggp-table th,.ggp-table td { padding:14px 16px; border-bottom:1px solid rgba(255,255,255,.08); text-align:right; white-space:nowrap; }
  .ggp-table th:first-child,.ggp-table td:first-child { text-align:left; position:sticky; left:0; background:#252a42; }
  .ggp-table th { color:rgba(255,255,255,.58); font-size:11px; text-transform:uppercase; letter-spacing:.08em; }
  .ggp-table td { font-size:14px; font-weight:700; }
  @media(max-width:900px){ .ggp-grid-3{grid-template-columns:1fr}.ggp-timeline{grid-template-columns:repeat(2,minmax(0,1fr))} }
  @media(max-width:560px){ .ggp-timeline{grid-template-columns:1fr}.ggp-hero{padding:38px 20px!important}.ggp-section{padding:24px 18px!important} }
`;

export default function GeneratorGrowthPricing() {
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState(null);
  const [account, setAccount] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let style = document.getElementById('generator-growth-pricing-css');
    if (!style) {
      style = document.createElement('style');
      style.id = 'generator-growth-pricing-css';
      document.head.appendChild(style);
    }
    style.textContent = PAGE_CSS;
    Promise.all([
      API.get('/v1/combat-commerce/catalogue'),
      API.get('/v1/combat-commerce/account'),
    ]).then(([catalogueRes, accountRes]) => {
      setCatalogue(catalogueRes.data.standalone_generators);
      setAccount(accountRes.data);
    }).catch((e) => setError(e.response?.data?.error || 'Unable to load generator growth and pricing.'));
  }, []);

  const projections = useMemo(
    () => catalogue?.projected_pricing || [],
    [catalogue],
  );
  const plans = catalogue?.plans || [];
  const extraChanges = catalogue?.extra_changes || [];
  const growth = account?.generator_growth;
  const progress = catalogue ? Math.min(100, catalogue.count * 100 / catalogue.planned_catalogue_capacity) : 0;
  const futureByYear = useMemo(() => projections.map((row) => ({
    ...row,
    prices: new Map((row.packs || []).map((pack) => [pack.key, pack.price_cents])),
  })), [projections]);
  const pack60 = plans.find((plan) => plan.key === 'pack60');

  if (!catalogue || !account) {
    return <div style={s.page}><ProductNav /><div style={s.container}><div style={s.section}>{error || 'Loading generator growth and pricing...'}</div></div></div>;
  }

  return (
    <div style={s.page}>
      <ProductNav />
      <SideHelpPanel title="Generator Growth &amp; Pricing">
        <strong style={s.helpHeading}>Two protections</strong>
        <p style={s.helpBody}>Your monthly price is locked while the subscription stays continuously active. Your selectable generator capacity grows separately after each completed year.</p>
        <div style={s.helpRule} />
        <strong style={s.helpHeading}>Future pricing</strong>
        <p style={s.helpBody}>Future values are planning positions only. They never activate on a timer. The owner must intentionally publish a new price version.</p>
      </SideHelpPanel>

      <main style={s.container}>
        <section className="ggp-hero" style={s.hero}>
          <div style={s.badge}>CURRENT CATALOGUE {catalogue.count} OF {catalogue.planned_catalogue_capacity}</div>
          <h1 style={s.heroTitle}>Your generator plan grows with your loyalty</h1>
          <p style={s.heroText}>Subscribe at today&apos;s monthly price and keep that price while your subscription remains continuously active. Every completed year adds two selectable generator slots, up to 200 total.</p>
          <div style={s.pills}>
            <span style={s.pill}>🔒 Fixed monthly price</span>
            <span style={s.pill}>📈 +2 slots each completed year</span>
            <span style={s.pill}>🎯 Up to 200 total slots</span>
          </div>
        </section>

        <section className="ggp-section" style={{ ...s.section, ...s.greenSection }}>
          <h2 style={s.title}>Why join now?</h2>
          <div className="ggp-grid-3">
            <Info number="01" title="Lock today&apos;s price">Your monthly subscription price stays fixed while the subscription remains continuously active.</Info>
            <Info number="02" title="Earn loyalty capacity">Every completed 12 months adds two selectable generator slots. No pack upgrade is required.</Info>
            <Info number="03" title="Grow beyond today&apos;s catalogue">Earned capacity can be higher than the number of generators currently available. It remains ready for future releases.</Info>
          </div>
        </section>

        <section className="ggp-section" style={{ ...s.section, ...s.flexSection }}>
          <div style={s.eyebrow}>90-DAY GENERATOR FLEXIBILITY</div>
          <h2 style={s.title}>Your campaign changes. Your generators can too.</h2>
          <div className="ggp-grid-3">
            <Info number="01" title="Keep what still works">Choose from the complete catalogue, keep the generators you want, and replace only those your campaign no longer needs.</Info>
            <Info number="02" title="Change every 90 days">A new change opportunity becomes available 90 days after a successful discretionary change. Unused opportunities do not stack.</Info>
            <Info number="03" title="Fill unused slots anytime">Newly purchased, deliberately empty, and annual loyalty slots can be filled without waiting and without resetting the change timer.</Info>
          </div>
          <p style={s.note}>Changing generator identities never changes your base pack, locked monthly price, subscription tenure, annual loyalty anniversary, or Stripe subscription.</p>
        </section>

        <section className="ggp-section" style={{ ...s.section, ...s.extraSection }}>
          <div style={s.eyebrow}>OPTIONAL EXTRA FLEXIBILITY</div>
          <h2 style={s.title}>Need to change sooner?</h2>
          <p style={s.copy}>Extra Generator Changes are one-time purchases for active subscribers. One credit covers one whole confirmed replacement event, even when several generators are replaced together. Credits accumulate and free changes are always used first.</p>
          <div className="ggp-grid-3">
            {extraChanges.map((pack) => <article key={pack.key} style={s.planCard}>
              <div style={s.planName}>{pack.name}</div>
              <div style={s.planPrice}>{money(pack.price_cents)}<span style={s.perMonth}> USD once</span></div>
              <div style={s.planBase}>{money(pack.per_change_cents)} per change</div>
              <div style={s.planGrowth}>{pack.saving_cents > 0 ? 'Save ' + money(pack.saving_cents) + ' compared with single changes' : 'One confirmed replacement event'}</div>
            </article>)}
          </div>
          <button type="button" style={{ ...s.button, ...glassSurface('#A66D05', '#F59E0B', '#FBBF24'), marginTop: 18 }}
            onClick={() => navigate('/dashboard/standalone-generators')}>Manage and buy extra changes</button>
        </section>

        {growth && (
          <section className="ggp-section" style={{ ...s.section, ...s.personalSection }}>
            <div style={s.sectionHead}>
              <div>
                <div style={s.eyebrow}>YOUR SUBSCRIPTION</div>
                <h2 style={s.title}>Your protected position</h2>
              </div>
              <button type="button" style={{ ...s.button, ...glassSurface('#334155', '#64748B', '#94A3B8') }} onClick={() => navigate('/dashboard/standalone-generators')}>Manage selected generators</button>
            </div>
            <div className="ggp-grid-3">
              <Metric label="Locked monthly price" value={money(growth.locked_monthly_price_cents)} detail="Protected while continuously active" />
              <Metric label="Generator capacity" value={`${growth.total_capacity}`} detail={`${growth.base_capacity} base + ${growth.loyalty_bonus_capacity} loyalty`} />
              <Metric label="Available to select now" value={`${growth.selectable_now}`} detail={`${growth.catalogue_available} generators currently available`} />
              <Metric label="Extra changes available" value={String(growth.extra_change_credits || 0)} detail="Used only while the free change is locked" />
            </div>
            <p style={s.note}>{growth.next_anniversary_at ? `Next loyalty anniversary: ${new Date(growth.next_anniversary_at).toLocaleDateString()}.` : 'Your generator capacity has reached the 200-slot maximum.'}</p>
          </section>
        )}

        <section className="ggp-section" style={s.section}>
          <div style={s.statusRow}>
            <div><div style={s.eyebrow}>AVAILABLE NOW</div><strong style={s.statusValue}>{catalogue.count} generators</strong></div>
            <div style={{ textAlign: 'right' }}><div style={s.eyebrow}>PLANNED CATALOGUE CAPACITY</div><strong style={{ ...s.statusValue, color: '#c084fc' }}>{catalogue.planned_catalogue_capacity} generators</strong></div>
          </div>
          <div style={s.track}><div style={{ ...s.fill, width: `${progress}%` }} /></div>
          <p style={s.note}>Capacity and availability are different. Loyalty slots can be earned before the catalogue contains enough generators to fill them.</p>
        </section>

        <section className="ggp-section" style={s.section}>
          <div style={s.eyebrow}>LOYALTY TIMELINE</div>
          <h2 style={s.title}>Two more slots after every completed year</h2>
          <div className="ggp-timeline">
            {[
              ['Start', '+0 slots', 'Your purchased base capacity'],
              ['Year 1', '+2 slots', 'First completed anniversary'],
              ['Year 3', '+6 slots', 'Six earned slots in total'],
              ['Year 5', '+10 slots', 'Ten earned slots in total'],
              ['Maximum', '200 total', 'Price lock continues at the cap'],
            ].map(([when, value, detail]) => <div key={when} style={s.timelineItem}><span style={s.timelineWhen}>{when}</span><strong style={s.timelineValue}>{value}</strong><span style={s.timelineDetail}>{detail}</span></div>)}
          </div>
        </section>

        <section className="ggp-section" style={s.section}>
          <div style={s.sectionHead}>
            <div><div style={s.eyebrow}>CURRENT PRICES</div><h2 style={s.title}>Choose-any generator plans</h2></div>
            <button type="button" style={{ ...s.button, ...glassSurface('#A66D05', '#F59E0B', '#FBBF24') }} onClick={() => navigate('/dashboard/standalone-generators')}>Choose generators</button>
          </div>
          <div className="ggp-plan-grid">
            {plans.map((plan) => <article key={plan.key} style={s.planCard}>
              <div style={s.planName}>{plan.name}</div>
              <div style={s.planPrice}>{money(plan.price_cents)}<span style={s.perMonth}> USD/mo</span></div>
              <div style={s.planBase}>{plan.capacity} base {plan.capacity === 1 ? 'slot' : 'slots'}</div>
              <div style={s.planGrowth}>Year 1: {plan.loyalty_capacity.year_1} · Year 3: {plan.loyalty_capacity.year_3} · Year 5: {plan.loyalty_capacity.year_5}</div>
            </article>)}
          </div>
        </section>

        <section className="ggp-section" style={s.section}>
          <div style={s.eyebrow}>PLANNED PRICE POSITIONS</div>
          <h2 style={s.title}>Individual price growth</h2>
          <p style={s.copy}>The planned individual position rises by $0.25 per year. These values are projections only. They do not activate automatically and do not change an active subscriber&apos;s locked price.</p>
          <div className="ggp-timeline">
            {futureByYear.map((row) => <div key={row.year_offset} style={{ ...s.timelineItem, ...(row.year_offset === 0 ? s.currentTimeline : null) }}>
              <span style={s.timelineWhen}>{row.calendar_year}{row.year_offset === 0 ? ' current' : ' projected'}</span>
              <strong style={s.timelineValue}>{money(row.individual_price_cents)}</strong>
              <span style={s.timelineDetail}>per generator each month</span>
            </div>)}
          </div>
        </section>

        <section className="ggp-section" style={s.section}>
          <div style={s.eyebrow}>FUTURE PACK TABLE</div>
          <h2 style={s.title}>Projected monthly pack prices</h2>
          <p style={s.copy}>Each projected pack preserves its current discount relationship to the individual price, then uses deterministic .99 rounding. Only an intentionally activated Stripe price version can become chargeable.</p>
          <div className="ggp-table-wrap">
            <table className="ggp-table">
              <thead><tr><th>Plan</th>{futureByYear.map((row) => <th key={row.year_offset}>{row.calendar_year}{row.year_offset ? ' projected' : ''}</th>)}</tr></thead>
              <tbody>
                {plans.map((plan) => <tr key={plan.key}>
                  <td>{plan.name}</td>
                  {futureByYear.map((row) => <td key={row.year_offset}>{money(plan.key === 'individual' ? row.individual_price_cents : row.prices.get(plan.key))}</td>)}
                </tr>)}
              </tbody>
            </table>
          </div>
        </section>

        {pack60 && (
          <section className="ggp-section" style={{ ...s.section, ...s.exampleSection }}>
            <div style={s.eyebrow}>GRANDFATHERING EXAMPLE</div>
            <h2 style={s.title}>A 60-generator pack joined today</h2>
            <div className="ggp-grid-3">
              <Metric label="Monthly price" value={money(pack60.price_cents)} detail="Stays fixed while continuously active" />
              <Metric label="After 3 years" value={`${pack60.loyalty_capacity.year_3} slots`} detail="60 base + 6 loyalty slots" />
              <Metric label="After 5 years" value={`${pack60.loyalty_capacity.year_5} slots`} detail="60 base + 10 loyalty slots" />
            </div>
            <p style={s.note}>Future public pack prices do not replace this customer&apos;s locked price. If the subscription ends, a later subscription starts at the then-current public price. Changing to a different plan locks that plan at the price shown when the change is confirmed.</p>
          </section>
        )}

        <section className="ggp-section" style={s.section}>
          <div style={s.eyebrow}>HOW GRANDFATHERING WORKS</div>
          <h2 style={s.title}>Price protection and loyalty growth are separate</h2>
          <div className="ggp-grid-3">
            <Info number="01" title="Price follows your active subscription">Your monthly price is the price accepted when that plan starts. Public price changes do not replace it.</Info>
            <Info number="02" title="Capacity follows completed anniversaries">Two slots are added only after each full 12-month anniversary, up to 200 total slots.</Info>
            <Info number="03" title="Continuity matters">Cancelling ends the price lock. A later subscription uses the public price available at that time.</Info>
          </div>
        </section>
      </main>
    </div>
  );
}

function Info({ number, title, children }) {
  return <div style={s.info}><span style={s.infoNumber}>{number}</span><div><strong style={s.infoTitle}>{title}</strong><p style={s.infoCopy}>{children}</p></div></div>;
}

function Metric({ label, value, detail }) {
  return <div style={s.metric}><span style={s.metricLabel}>{label}</span><strong style={s.metricValue}>{value}</strong><span style={s.metricDetail}>{detail}</span></div>;
}

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg,#1a1840 0%,#2d3a4a 50%,#701030 100%)', fontFamily: "'Segoe UI',sans-serif", paddingBottom: 64, color: '#fff' },
  container: { maxWidth: 1560, margin: '0 auto', padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 28 },
  hero: { background: 'linear-gradient(135deg,#1e1b4b 0%,#312e81 100%)', border: '1px solid rgba(139,92,246,.35)', borderRadius: 20, padding: '56px 40px', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,.48)' },
  badge: { display: 'inline-block', color: '#c084fc', background: 'rgba(139,92,246,.2)', border: '1px solid rgba(139,92,246,.5)', borderRadius: 20, padding: '6px 15px', fontSize: 11, fontWeight: 800, letterSpacing: '.11em' },
  heroTitle: { fontSize: 'clamp(30px,4vw,48px)', margin: '20px 0 16px', lineHeight: 1.14 },
  heroText: { maxWidth: 820, margin: '0 auto 28px', color: 'rgba(255,255,255,.76)', fontSize: 17, lineHeight: 1.75 },
  pills: { display: 'flex', justifyContent: 'center', gap: 11, flexWrap: 'wrap' },
  pill: { background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 30, padding: '9px 18px', fontSize: 13, fontWeight: 700 },
  section: { background: 'rgba(255,255,255,.055)', border: '1px solid rgba(255,255,255,.11)', borderRadius: 16, padding: '32px 36px', boxShadow: '0 5px 28px rgba(0,0,0,.24)' },
  greenSection: { background: 'rgba(16,185,129,.08)', borderColor: 'rgba(16,185,129,.24)' },
  personalSection: { background: 'linear-gradient(135deg,rgba(49,46,129,.62),rgba(6,95,70,.42))', borderColor: 'rgba(167,139,250,.4)' },
  exampleSection: { background: 'linear-gradient(135deg,rgba(88,28,135,.38),rgba(159,18,57,.28))' },
  flexSection: { background: 'linear-gradient(135deg,rgba(14,116,144,.22),rgba(88,28,135,.28))', borderColor: 'rgba(34,211,238,.25)' },
  extraSection: { background: 'linear-gradient(135deg,rgba(180,83,9,.2),rgba(88,28,135,.28))', borderColor: 'rgba(245,158,11,.3)' },
  title: { margin: '5px 0 22px', fontSize: 26, lineHeight: 1.25 },
  eyebrow: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.11em', color: 'rgba(255,255,255,.48)', fontWeight: 800 },
  copy: { color: 'rgba(255,255,255,.72)', lineHeight: 1.65, margin: '-9px 0 22px' },
  info: { display: 'flex', gap: 15, alignItems: 'flex-start' },
  infoNumber: { color: '#10b981', opacity: .55, fontWeight: 900, fontSize: 24 },
  infoTitle: { display: 'block', fontSize: 16, marginBottom: 7 },
  infoCopy: { color: 'rgba(255,255,255,.7)', lineHeight: 1.6, margin: 0, fontSize: 14 },
  sectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' },
  button: { borderRadius: 10, padding: '11px 17px', fontWeight: 800, cursor: 'pointer' },
  metric: { background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 13, padding: 20, display: 'flex', flexDirection: 'column', gap: 6 },
  metricLabel: { color: 'rgba(255,255,255,.5)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 800 },
  metricValue: { fontSize: 28, color: '#fff' },
  metricDetail: { color: 'rgba(255,255,255,.65)', fontSize: 13 },
  note: { color: 'rgba(255,255,255,.58)', fontSize: 13, lineHeight: 1.6, margin: '18px 0 0' },
  statusRow: { display: 'flex', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', marginBottom: 17 },
  statusValue: { display: 'block', fontSize: 23, marginTop: 5 },
  track: { height: 11, borderRadius: 8, background: 'rgba(255,255,255,.09)', overflow: 'hidden' },
  fill: { height: '100%', background: 'linear-gradient(90deg,#7c3aed,#c084fc)', borderRadius: 8 },
  timelineItem: { background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 12, padding: 17, display: 'flex', flexDirection: 'column', gap: 6 },
  currentTimeline: { borderColor: 'rgba(16,185,129,.65)', background: 'rgba(16,185,129,.1)' },
  timelineWhen: { color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: '.07em', fontSize: 10, fontWeight: 800 },
  timelineValue: { fontSize: 21, color: '#fff' },
  timelineDetail: { color: 'rgba(255,255,255,.56)', fontSize: 12, lineHeight: 1.45 },
  planCard: { background: 'rgba(255,255,255,.055)', border: '1px solid rgba(255,255,255,.11)', borderTop: '3px solid #8b5cf6', borderRadius: 13, padding: 18 },
  planName: { color: '#c4b5fd', fontWeight: 800, fontSize: 14 },
  planPrice: { fontSize: 27, fontWeight: 900, margin: '8px 0 5px' },
  perMonth: { fontSize: 11, color: 'rgba(255,255,255,.48)', fontWeight: 600 },
  planBase: { color: 'rgba(255,255,255,.72)', fontSize: 13 },
  planGrowth: { color: '#6ee7b7', fontSize: 11, lineHeight: 1.55, marginTop: 10 },
  helpHeading: { display: 'block', textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 12 },
  helpBody: { color: 'rgba(255,255,255,.72)', fontSize: 13, lineHeight: 1.55 },
  helpRule: { height: 1, background: 'rgba(255,255,255,.08)', margin: '14px 0' },
};
